// A setup card for a destination whose credentials are just fields the
// merchant pastes.
//
// Five of the six existing cards are the same component with different
// labels: draft-backed text fields, a canSubmit rule that differs between
// first-time setup and editing, Test and Save, and a replay note on save.
// Each new destination copying that by hand is ~140 lines where the only real
// content is which fields exist, and a copy is where a subtle difference
// creeps in (a missing trim, the wrong canSubmit rule, a forgotten
// clearDraftFields that leaves a stale token in the next card the merchant
// opens).
//
// So the five new destinations are config, not components. Google Ads stays
// hand-written because its flow is genuinely different (an OAuth grant, not a
// paste), and Meta/GA4 stay as they are because their copy is long-form and
// their tests slice their source.

import { useState, type ReactNode } from "react";
import { Card, Text, BlockStack, InlineStack, Button, TextField } from "@shopify/polaris";
import { apiFetch, ApiError } from "../api";
import { withReplayNote } from "../replay-note";
import {
  useDraftField,
  clearDraftFields,
  StatusBanner,
  OverwriteWarning,
  DestinationStatusBadge,
  TestCodeNotice,
  type CardState,
} from "../Onboarding";
import { DESTINATION_CATALOG, type DestinationType } from "../destination-catalog";
// From ./types, NOT ./index: index imports every card, and each generated
// card calls tokenDestinationCard at module scope, so importing from index
// re-enters this module mid-initialization and DESTINATION_CATALOG reads as
// undefined. See the note in ./types.ts.
import type { DestinationCardProps } from "./types";

export interface TokenCardField {
  /** The credential key the backend's Zod schema expects. */
  key: string;
  label: string;
  /** Renders as a password field. Must also be `secret` in the adapter's credentialHints. */
  secret?: boolean;
  placeholder?: string;
  helpText?: string;
  /**
   * Not required for a first-time save (a test event code, say). Optional
   * fields never gate canSubmit, so a destination whose every field is
   * optional could never be saved -- which the config test forbids.
   */
  optional?: boolean;
}

export interface TokenCardConfig {
  type: DestinationType;
  /** The paragraph under the title: what this does and where to find the credentials. */
  intro: ReactNode;
  fields: TokenCardField[];
  /** What clicking Test actually does on the platform, if it needs saying. */
  testNote?: ReactNode;
}

/**
 * Build a destination card from a config.
 *
 * Returns a component, so a destination's module is one `export default
 * tokenDestinationCard({...})`.
 */
/**
 * Whether Save may be clicked, given what is typed.
 *
 * Extracted and exported because this is the card's one real decision, and
 * getting it wrong is silent in both directions: too strict and a merchant
 * cannot rotate a single token without re-typing the others, too loose and a
 * first-time save posts a half-filled credential set that the backend then
 * rejects with a Zod error the merchant cannot act on.
 */
export function tokenCardCanSubmit(
  fields: readonly TokenCardField[],
  value: (key: string) => string,
  configured: boolean,
): boolean {
  const filled = (f: TokenCardField) => value(f.key).trim().length > 0;
  // Editing: one field is enough, because the backend's partial-update merge
  // preserves the rest.
  if (configured) return fields.some(filled);
  // First-time setup: every required field. Optional ones never gate it.
  return fields.filter((f) => f.optional !== true).every(filled);
}

/**
 * The POST body: every field, trimmed, with one asymmetry that matters.
 *
 * The route's partial-update merge reads an empty string as "preserve the
 * stored value" and a null as "overwrite". That is what lets a merchant
 * rotate one credential without re-typing the others, so a blanked REQUIRED
 * field must stay an empty string.
 *
 * An OPTIONAL field is the opposite case. A merchant who clears one means to
 * clear it, and sending "" preserved the old value instead, so a saved test
 * event code could never be removed through this card: events would stay
 * routed to the platform's test view and never reach live reporting, with
 * the UI showing an empty box the whole time. Blanked optional fields are
 * therefore sent as null.
 */
export function tokenCardBody(
  fields: readonly TokenCardField[],
  value: (key: string) => string,
): Record<string, string | null> {
  return Object.fromEntries(
    fields.map((f) => {
      const v = value(f.key).trim();
      if (v === "" && f.optional === true) return [f.key, null];
      return [f.key, v];
    }),
  );
}

/**
 * Reject a config that could never work, at module load rather than when a
 * merchant opens the card.
 *
 * The all-optional case is the one that matters: `every` over an empty list
 * is true, so a config with no required field enables Save on an empty form
 * and posts a credential set with nothing in it.
 */
export function validateTokenCardConfig(config: TokenCardConfig): void {
  if (config.fields.length === 0) {
    throw new Error(`${config.type} token card: no fields`);
  }
  if (config.fields.every((f) => f.optional === true)) {
    throw new Error(
      `${config.type} token card: every field is optional, so Save would be enabled on an empty form`,
    );
  }
  const keys = config.fields.map((f) => f.key);
  if (new Set(keys).size !== keys.length) {
    // Two fields on one key would share a draft and overwrite each other.
    throw new Error(`${config.type} token card: duplicate field keys`);
  }
}

export function tokenDestinationCard(config: TokenCardConfig) {
  validateTokenCardConfig(config);
  // The catalog is read at RENDER time, not here. Each generated card calls
  // this function at module scope, and this module sits in a cycle
  // (TokenDestinationCard -> Onboarding -> destination-cards/index -> a
  // generated card -> back here). Reading an imported binding during that
  // re-entry throws "Cannot access ... before initialization"; by first
  // render every module is initialized. validateTokenCardConfig stays eager
  // because it touches only the config literal.

  function TokenCard({ shopify, onSaved, existingDestination }: DestinationCardProps) {
    const entry = DESTINATION_CATALOG[config.type];
    // One draft hook per field, in a fixed order: the config is a module
    // constant, so the hook count never changes between renders.
    const drafts = config.fields.map((f) => useDraftField(config.type, f.key));
    const [state, setState] = useState<CardState>({ kind: "idle" });

    const valueOf = (key: string) => {
      const i = config.fields.findIndex((f) => f.key === key);
      return drafts[i]![0].trim();
    };

    const canSubmit = tokenCardCanSubmit(
      config.fields,
      valueOf,
      existingDestination?.configured === true,
    );

    const buildBody = () => tokenCardBody(config.fields, valueOf);

    const run = async (what: "test" | "save") => {
      setState({ kind: "working", what });
      const url =
        what === "test"
          ? `/api/destinations/${config.type}/test`
          : `/api/destinations/${config.type}`;
      try {
        const resp = await apiFetch<{ ok?: boolean; status?: number }>(shopify, url, {
          method: "POST",
          body: JSON.stringify(buildBody()),
        });
        if (what === "test") {
          setState({
            kind: "success",
            message: `${entry.name} accepted the test event (HTTP ${resp.status}).`,
          });
          return;
        }
        setState({
          kind: "success",
          message: withReplayNote(`${entry.name} saved.`, config.type, resp),
        });
        // Clearing matters: a draft left behind shows a stale token in the
        // next card the merchant opens for this destination.
        clearDraftFields(config.type);
        onSaved();
      } catch (err) {
        const msg = err instanceof ApiError ? err.message : (err as Error).message;
        setState({
          kind: "error",
          message: `${what === "test" ? "Test" : "Save"} failed: ${msg}`,
        });
      }
    };

    return (
      <Card>
        <BlockStack gap="400">
          <InlineStack align="space-between" blockAlign="center">
            <Text as="h2" variant="headingMd">
              {entry.label}
            </Text>
            <DestinationStatusBadge existing={existingDestination} />
          </InlineStack>
          <Text as="p" tone="subdued">
            {config.intro}
          </Text>

          <OverwriteWarning existing={existingDestination} />
          {/*
            Renders only when a code is actually saved. Without it, a
            destination that stores a test event code (Reddit) would warn the
            merchant nowhere that its events are going to the platform's test
            view instead of its reporting.
          */}
          {entry.notice?.kind === "paste" && entry.notice.testEventCode ? (
            <TestCodeNotice
              shopify={shopify}
              type={config.type}
              existing={existingDestination}
              onCleared={(message) => {
                if (message) setState({ kind: "success", message });
                onSaved();
              }}
            />
          ) : null}

          {config.fields.map((f, i) => (
            <TextField
              key={f.key}
              label={f.label}
              value={drafts[i]![0]}
              onChange={drafts[i]![1]}
              autoComplete={f.secret ? "new-password" : "off"}
              {...(f.secret ? { type: "password" as const } : {})}
              {...(f.placeholder ? { placeholder: f.placeholder } : {})}
              {...(f.helpText ? { helpText: f.helpText } : {})}
            />
          ))}

          {config.testNote ? (
            <Text as="p" tone="subdued" variant="bodySm">
              {config.testNote}
            </Text>
          ) : null}

          <StatusBanner state={state} />

          <InlineStack gap="200">
            <Button
              onClick={() => void run("test")}
              disabled={
                !(existingDestination?.configured || canSubmit) || state.kind === "working"
              }
              loading={state.kind === "working" && state.what === "test"}
            >
              {existingDestination?.configured ? "Send test event" : "Test connection"}
            </Button>
            <Button
              variant="primary"
              onClick={() => void run("save")}
              disabled={!canSubmit || state.kind === "working"}
              loading={state.kind === "working" && state.what === "save"}
            >
              {existingDestination?.configured ? "Save changes" : "Save"}
            </Button>
          </InlineStack>
        </BlockStack>
      </Card>
    );
  }

  TokenCard.displayName = `TokenDestinationCard(${config.type})`;
  TokenCard.config = config;
  return TokenCard;
}
